import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, X, Trash2, Pencil, FilePlus2, FileText, MapPin, User, Clock, Loader2, Navigation } from "lucide-react";
import api, { apiError } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { ProLocked, CompanyPicker } from "../components/ProGate";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { canPlan, hasPro, isProRequired, useProScope, STATUS_COLORS } from "../lib/pro";
import { inputCls } from "./NewReport";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parse = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const addDays = (s, n) => { const d = parse(s); d.setDate(d.getDate() + n); return ymd(d); };
const monday = (s) => { const d = parse(s); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return ymd(d); };
const STATUS_LABEL = { planned: "Planned", in_progress: "In progress", done: "Done" };
const EMPTY = { mechanic_id: "", customer_name: "", address: "", contact_person: "", call_id: "", date: "", start_time: "08:00", end_time: "12:00", machines: [], notes: "" };

export default function Planning() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const scope = useProScope(user);
  const planner = canPlan(user);
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(ymd(new Date()));
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [team, setTeam] = useState([]);
  const [types, setTypes] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);

  const start = view === "week" ? monday(anchor) : anchor;
  const end = view === "week" ? addDays(start, 6) : anchor;
  const days = view === "week" ? [0, 1, 2, 3, 4, 5, 6].map((i) => addDays(start, i)) : [anchor];
  const locale = lang === "nl" ? "nl-NL" : "en-GB";

  const load = () => {
    if (!hasPro(user) || scope.needsCompany) return;
    setLoading(true);
    api.get("/pro/jobs", { params: { start, end, ...scope.params } })
      .then(({ data }) => { setJobs(data); setLocked(false); })
      .catch((e) => { setJobs([]); if (isProRequired(e)) setLocked(true); else toast.error(apiError(e)); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [start, end, user, scope.cid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onFocus);
    return () => document.removeEventListener("visibilitychange", onFocus);
  }, [start, end, user, scope.cid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!planner || scope.needsCompany) return;
    api.get("/pro/team", { params: scope.params }).then(({ data }) => setTeam(data)).catch(() => {});
    api.get("/pro/machine-types", { params: scope.params }).then(({ data }) => setTypes(data)).catch(() => {});
  }, [planner, scope.cid]); // eslint-disable-line react-hooks/exhaustive-deps

  const title = planner ? t("Planning") : t("My jobs");
  if (!hasPro(user)) return <><PageHeader title={title} /><ProLocked /></>;

  const mechName = (j) => j.mechanic_name || team.find((m) => m.id === j.mechanic_id)?.name || "";
  const step = view === "week" ? 7 : 1;
  const label = view === "week"
    ? `${parse(start).toLocaleDateString(locale, { day: "numeric", month: "short" })} – ${parse(end).toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" })}`
    : parse(anchor).toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <>
      <PageHeader title={title} subtitle={planner ? t("Plan jobs for your mechanics") : t("Your planned jobs")}
        right={planner && !scope.needsCompany && !locked && <button onClick={() => setEditing({ ...EMPTY, date: anchor })} data-testid="new-job-btn" className="bg-[#F26722] hover:bg-[#D95513] text-white text-sm font-bold px-3 py-2 rounded-lg flex items-center gap-1.5 whitespace-nowrap transition-colors"><Plus size={16} /> {t("New job")}</button>} />
      {scope.isSA && <CompanyPicker scope={scope} />}
      {scope.needsCompany ? <p className="text-zinc-500 px-4 md:px-0 mt-4 text-sm">{t("Choose a company to view its planning.")}</p> : locked ? <ProLocked /> : (
        <div className="px-4 md:px-0 pt-4">
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <div className="flex items-center gap-1">
              <button onClick={() => setAnchor(addDays(anchor, -step))} data-testid="planning-prev" className="p-2 rounded-lg bg-[#18181B] border border-zinc-800 text-zinc-300 hover:text-white"><ChevronLeft size={18} /></button>
              <button onClick={() => setAnchor(ymd(new Date()))} data-testid="planning-today" className="px-3 py-2 rounded-lg bg-[#18181B] border border-zinc-800 text-sm font-semibold text-zinc-300 hover:text-white">{t("Today")}</button>
              <button onClick={() => setAnchor(addDays(anchor, step))} data-testid="planning-next" className="p-2 rounded-lg bg-[#18181B] border border-zinc-800 text-zinc-300 hover:text-white"><ChevronRight size={18} /></button>
              <span className="text-sm font-semibold text-white ml-2" data-testid="planning-range-label">{label}</span>
            </div>
            <div className="inline-flex rounded-xl border border-zinc-800 overflow-hidden" data-testid="planning-view-toggle">
              {[["day", t("Day")], ["week", t("Week")]].map(([v, l]) => (
                <button key={v} onClick={() => setView(v)} data-testid={`planning-view-${v}`} className={`px-4 py-2 text-sm font-bold transition-colors ${view === v ? "bg-[#F26722] text-white" : "bg-[#18181B] text-zinc-400 hover:text-white"}`}>{l}</button>
              ))}
            </div>
          </div>
          <Legend t={t} />
          {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-zinc-600" /></div> : (
            <div className={`grid gap-3 mt-4 ${view === "week" ? "md:grid-cols-7" : ""}`} data-testid="planning-grid">
              {days.map((d) => {
                const dj = jobs.filter((j) => j.date === d).sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
                const isToday = d === ymd(new Date());
                return (
                  <div key={d} className={`bg-[#141416] border rounded-2xl p-2.5 min-h-[120px] ${isToday ? "border-[#F26722]/50" : "border-zinc-800"}`} data-testid={`planning-day-${d}`}>
                    <p className={`text-xs font-bold uppercase tracking-wider px-1 mb-2 ${isToday ? "text-[#F26722]" : "text-zinc-500"}`}>{parse(d).toLocaleDateString(locale, { weekday: "short", day: "numeric", month: "short" })}</p>
                    <div className="space-y-2">
                      {dj.map((j) => <JobCard key={j.id} j={j} mech={mechName(j)} t={t} onClick={() => setViewing(j)} />)}
                      {dj.length === 0 && <p className="text-[11px] text-zinc-700 px-1">—</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {!loading && jobs.length === 0 && <p className="text-sm text-zinc-500 mt-4" data-testid="planning-empty">{planner ? t("No jobs planned in this period.") : t("No jobs planned for you in this period.")}</p>}
        </div>
      )}
      {viewing && <JobDetail job={viewing} mech={mechName(viewing)} types={types} planner={planner} user={user} t={t} onClose={() => setViewing(null)}
        onEdit={() => { setEditing(viewing); setViewing(null); }} onDeleted={() => { setViewing(null); load(); }} scope={scope} />}
      {editing && <JobForm job={editing} team={team} types={types} t={t} scope={scope} user={user} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </>
  );
}

function Legend({ t }) {
  return (
    <div className="flex flex-wrap gap-4 mt-3" data-testid="planning-legend">
      {Object.entries(STATUS_COLORS).map(([k, c]) => <span key={k} className="flex items-center gap-1.5 text-xs text-zinc-400"><span className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />{t(STATUS_LABEL[k])}</span>)}
    </div>
  );
}

const isAppleMobile = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const routeUrl = (address) => {
  const a = encodeURIComponent(address.trim());
  return isAppleMobile() ? `https://maps.apple.com/?daddr=${a}` : `https://www.google.com/maps/dir/?api=1&destination=${a}`;
};

function NavigateButton({ address, testid, t }) {
  if (!address?.trim()) return null;
  return (
    <a href={routeUrl(address)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} data-testid={testid}
      className="inline-flex items-center gap-1 text-[11px] font-bold text-[#3D6080] bg-[#3D6080]/10 hover:bg-[#3D6080]/20 px-2 py-1 rounded-md transition-colors shrink-0">
      <Navigation size={12} /> {t("Navigate")}
    </a>
  );
}

function JobCard({ j, mech, t, onClick }) {
  const c = STATUS_COLORS[j.status] || STATUS_COLORS.planned;
  return (
    <div className="rounded-xl border-l-4 transition-transform hover:-translate-y-0.5" style={{ borderColor: c, background: `${c}22` }}>
      <button onClick={onClick} data-testid={`job-card-${j.id}`} className="w-full text-left p-2.5 pb-1.5">
        <p className="text-[11px] font-bold" style={{ color: c }}>{j.start_time}–{j.end_time} · {t(STATUS_LABEL[j.status] || "Planned")}</p>
        <p className="text-sm font-bold text-white truncate">{j.customer_name}</p>
        {mech && <p className="text-[11px] text-zinc-400 truncate">{mech}</p>}
        {j.address && <p className="text-[11px] text-zinc-500 truncate">{j.address}</p>}
      </button>
      {j.address?.trim() && <div className="px-2.5 pb-2"><NavigateButton address={j.address} t={t} testid={`job-card-navigate-${j.id}`} /></div>}
      {!j.address?.trim() && <div className="pb-1" />}
    </div>
  );
}

function Modal({ children, onClose, testid }) {
  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid={testid} className="bg-[#0A0A0A] border border-zinc-800 w-full md:max-w-lg max-h-[90vh] overflow-y-auto rounded-t-2xl md:rounded-2xl p-5 relative">
        <button onClick={onClose} data-testid={`${testid}-close`} className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X size={20} /></button>
        {children}
      </div>
    </div>,
    document.body
  );
}

function JobDetail({ job, mech, types, planner, user, t, onClose, onEdit, onDeleted, scope }) {
  const c = STATUS_COLORS[job.status] || STATUS_COLORS.planned;
  const typeName = (id) => types.find((x) => x.id === id)?.name;
  const del = async () => {
    if (!window.confirm(t("Delete this job?"))) return;
    try { await api.delete(`/pro/jobs/${job.id}`, { params: scope.params }); toast.success(t("Job deleted")); onDeleted(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Modal onClose={onClose} testid="job-detail">
      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${c}33`, color: c }} data-testid="job-detail-status">{t(STATUS_LABEL[job.status] || "Planned")}</span>
      <h2 className="text-xl font-extrabold text-white mt-2 pr-8">{job.customer_name}</h2>
      <div className="mt-3 space-y-1.5 text-sm text-zinc-300">
        <p className="flex items-center gap-2"><Clock size={14} className="text-[#F26722]" />{job.date} · {job.start_time}–{job.end_time}</p>
        {mech && <p className="flex items-center gap-2"><User size={14} className="text-[#F26722]" />{mech}</p>}
        {job.address?.trim() && <p className="flex items-center gap-2 flex-wrap"><MapPin size={14} className="text-[#F26722]" />{job.address}<NavigateButton address={job.address} t={t} testid="job-detail-navigate" /></p>}
        {job.contact_person && <p className="text-zinc-400">{t("Contact person")}: {job.contact_person}</p>}
        {job.call_id && <p className="text-zinc-400">{t("Call ID")}: {job.call_id}</p>}
      </div>
      {(job.machines || []).length > 0 && (
        <div className="mt-3 space-y-1">
          {job.machines.map((m, i) => <p key={i} className="text-xs bg-[#18181B] rounded-lg px-3 py-2 text-zinc-300 font-mono">{m.serial_number || "—"}{m.machine_type_id && typeName(m.machine_type_id) ? <span className="font-sans text-zinc-500"> · {typeName(m.machine_type_id)}</span> : ""}</p>)}
        </div>
      )}
      {job.notes && <p className="text-sm text-zinc-400 mt-3 whitespace-pre-wrap">{job.notes}</p>}
      <div className="flex flex-wrap gap-2 mt-5">
        <JobReportButton job={job} user={user} t={t} scope={scope} />
        {planner && <button onClick={onEdit} data-testid="job-edit" className="bg-[#18181B] border border-[#F26722]/50 text-[#F26722] text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5"><Pencil size={16} /> {t("Edit")}</button>}
        {planner && <button onClick={del} data-testid="job-delete" className="bg-[#18181B] border border-red-500/40 text-red-400 text-sm font-semibold px-3 py-2 rounded-lg flex items-center gap-1.5"><Trash2 size={16} /> {t("Delete")}</button>}
      </div>
    </Modal>
  );
}

// Mechanics and managers can start a report from a job; office never creates reports.
const canStartReport = (user) => ["mechanic", "manager"].includes(user?.role);

function JobReportButton({ job, user, t, scope, full, dirty }) {
  const nav = useNavigate();
  const size = full ? "w-full justify-center py-3 rounded-xl" : "px-3 py-2 rounded-lg";
  if (job.report_id) return <button type="button" onClick={() => nav(`/report/${job.report_id}`)} data-testid={full ? "job-form-view-report" : "job-open-report"} className={`bg-[#18181B] border border-zinc-700 text-white text-sm font-semibold flex items-center gap-1.5 ${size}`}><FileText size={16} /> {t("View report")}</button>;
  if (!canStartReport(user)) return null;
  const go = () => { if (dirty && !window.confirm(t("You have unsaved changes to this job. Leave without saving?"))) return; nav(`/new?job_id=${encodeURIComponent(job.id)}${scope.suffix ? `&company_id=${encodeURIComponent(scope.cid)}` : ""}`); };
  const style = full ? "border-2 border-[#F26722] text-[#F26722] hover:bg-orange-50" : "bg-[#F26722] hover:bg-[#D95513] text-white";
  return <button type="button" onClick={go} data-testid={full ? "job-form-start-report" : "job-start-report"} className={`${style} text-sm font-bold flex items-center gap-1.5 ${size}`}><FilePlus2 size={16} /> {t("Start report")}</button>;
}

function JobForm({ job, team, types, t, scope, onClose, onSaved, user }) {
  const [f, setF] = useState({ ...EMPTY, ...job, machines: (job.machines || []).map((m) => ({ serial_number: m.serial_number || "", machine_type_id: m.machine_type_id || null })) });
  const [saving, setSaving] = useState(false);
  const [initial] = useState(() => JSON.stringify(f));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setM = (i, k, v) => setF({ ...f, machines: f.machines.map((m, j) => j === i ? { ...m, [k]: v } : m) });
  const save = async () => {
    if (!f.mechanic_id) return toast.error(t("Choose a mechanic"));
    if (!f.customer_name.trim()) return toast.error(t("Customer name is required"));
    if (!f.date) return toast.error(t("Choose a date"));
    if (f.start_time && f.end_time && f.end_time <= f.start_time) return toast.error(t("End time must be after start time"));
    const body = { mechanic_id: f.mechanic_id, customer_name: f.customer_name.trim(), address: f.address, contact_person: f.contact_person, call_id: f.call_id, date: f.date, start_time: f.start_time, end_time: f.end_time, machines: f.machines.filter((m) => m.serial_number.trim() || m.machine_type_id), notes: f.notes };
    setSaving(true);
    try {
      if (job.id) await api.put(`/pro/jobs/${job.id}`, body, { params: scope.params });
      else await api.post("/pro/jobs", body, { params: scope.params });
      toast.success(job.id ? t("Job updated") : t("Job planned"));
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const lbl = "text-xs text-zinc-400";
  return (
    <Modal onClose={onClose} testid="job-form">
      <h2 className="text-xl font-extrabold text-white pr-8">{job.id ? t("Edit job") : t("New job")}</h2>
      <div className="space-y-3 mt-4">
        <div><label className={lbl}>{t("Mechanic")} *</label>
          <select className={inputCls} value={f.mechanic_id} onChange={set("mechanic_id")} data-testid="job-mechanic-select">
            <option value="">{t("Choose a mechanic")}</option>
            {team.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
          </select></div>
        <div><label className={lbl}>{t("Customer name")} *</label><input className={inputCls} value={f.customer_name} onChange={set("customer_name")} data-testid="job-customer-input" /></div>
        <div><label className={lbl}>{t("Address")}</label><input className={inputCls} value={f.address} onChange={set("address")} data-testid="job-address-input" /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className={lbl}>{t("Contact person")}</label><input className={inputCls} value={f.contact_person} onChange={set("contact_person")} data-testid="job-contact-input" /></div>
          <div><label className={lbl}>{t("Call ID")}</label><input className={inputCls} value={f.call_id} onChange={set("call_id")} data-testid="job-callid-input" /></div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div><label className={lbl}>{t("Date")} *</label><input type="date" className={inputCls + " px-2"} value={f.date} onChange={set("date")} data-testid="job-date-input" /></div>
          <div><label className={lbl}>{t("Start")}</label><input type="time" className={inputCls + " px-2"} value={f.start_time} onChange={set("start_time")} data-testid="job-start-input" /></div>
          <div><label className={lbl}>{t("End")}</label><input type="time" className={inputCls + " px-2"} value={f.end_time} onChange={set("end_time")} data-testid="job-end-input" /></div>
        </div>
        <div>
          <label className={lbl}>{t("Machines")}</label>
          {f.machines.map((m, i) => (
            <div key={i} className="flex gap-2 mt-1.5">
              <input className={inputCls} placeholder={t("Serial number")} value={m.serial_number} onChange={(e) => setM(i, "serial_number", e.target.value)} data-testid={`job-machine-serial-${i}`} />
              <select className={inputCls} value={m.machine_type_id || ""} onChange={(e) => setM(i, "machine_type_id", e.target.value || null)} data-testid={`job-machine-type-${i}`}>
                <option value="">{t("Machine type")}: {t("None")}</option>
                {types.map((mt) => <option key={mt.id} value={mt.id}>{mt.name}</option>)}
              </select>
              <button onClick={() => setF({ ...f, machines: f.machines.filter((_, j) => j !== i) })} className="text-zinc-500 shrink-0"><Trash2 size={16} /></button>
            </div>
          ))}
          <button onClick={() => setF({ ...f, machines: [...f.machines, { serial_number: "", machine_type_id: null }] })} data-testid="job-add-machine" className="text-[#F26722] text-sm font-semibold mt-2 flex items-center gap-1"><Plus size={15} /> {t("Add machine")}</button>
        </div>
        <div><label className={lbl}>{t("Notes")}</label><textarea className={inputCls + " min-h-20"} value={f.notes} onChange={set("notes")} data-testid="job-notes-input" /></div>
      </div>
      <button onClick={save} disabled={saving} data-testid="job-save-btn" className="w-full bg-[#F26722] hover:bg-[#D95513] disabled:opacity-60 text-white font-bold py-3.5 rounded-xl mt-5 flex items-center justify-center gap-2">
        {saving ? <Loader2 className="animate-spin" size={18} /> : t("Save job")}
      </button>
      {job.id && <div className="mt-2"><JobReportButton job={job} user={user} t={t} scope={scope} full dirty={JSON.stringify(f) !== initial} /></div>}
    </Modal>
  );
}
